import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import strings from "translate/language";
import {
  formatAppointmentCellValue,
  getClosingStatusBadgeClass,
  resolveCheckInDisplay,
  resolveClosingStatusLabel,
} from "utils/appointmentListStatusDisplay";
import StatusBadge, { CheckInStatusBadge } from "component/common/StatusBadge";
import { handleListResponse, seo } from "utils/utils";
import { getAppointmentList } from "../../reduxStore/action/appointmentActions";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import TableListSkeleton from "component/common/TableListSkeleton";
import "./AppointmentListing.css";

const renderAppointmentCell = (value, toneClass = "") => {
  const text = formatAppointmentCellValue(value);
  const tone = text === "-" ? "appointment-listing__empty" : toneClass;
  return (
    <span className={`appointment-listing__cell ${tone}`.trim()}>{text}</span>
  );
};

const renderDateTimeCell = (dateValue, timeValue = "") => {
  if (!dateValue && !timeValue) {
    return <span className="appointment-listing__empty">-</span>;
  }
  return (
    <span className="appointment-listing__date-stack">
      {dateValue ? (
        <span className="appointment-listing__date-day">{dateValue}</span>
      ) : null}
      {timeValue ? (
        <span className="appointment-listing__date-time">{timeValue}</span>
      ) : null}
    </span>
  );
};

const TodayAppointment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appointmentListSelector = useSelector((state) => state.appointment);
  const { loading, response } = appointmentListSelector;

  const [appointmentData, setAppointmentData] = useState([]);
  const [listReady, setListReady] = useState(false);

  const { view, create, edit } = usePermission({
    view: "view_appointment",
    create: "add_appointment",
    edit: "edit_appointment",
  });

  const formatDate = (date) => moment(date).format("YYYY-MM-DD");
  const todayStr = formatDate(new Date());

  useEffect(() => {
    setListReady(false);
    dispatch(
      getAppointmentList({
        start_date: todayStr,
        end_date: todayStr,
        status: 3,
        limit: "",
        offset: 0,
      }),
    );
  }, [dispatch, todayStr]);

  useEffect(() => {
    const responseData = response;
    if (responseData?.status === 200) {
      const data = responseData?.data || [];
      const safe = Array.isArray(data) ? data.slice() : [];
      safe.sort((a, b) => {
        const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
        if (tb !== ta) return tb - ta;
        const da = a?.appointment_date
          ? new Date(a.appointment_date).getTime()
          : 0;
        const db = b?.appointment_date
          ? new Date(b.appointment_date).getTime()
          : 0;
        if (db !== da) return db - da;
        return String(b?._id || "").localeCompare(String(a?._id || ""));
      });
      const cleaned = handleListResponse(safe);
      setAppointmentData(Array.isArray(cleaned) ? cleaned : []);
    } else {
      setAppointmentData([]);
    }
    setListReady(true);
  }, [response]);

  useEffect(() => {
    seo({
      title: strings.todayAppointments,
      metaDescription: strings.todayAppointmentsSubtitle,
      canonicalLink: window.location.href,
    });
  }, []);

  const showSkeleton = loading && !listReady;

  return (
    <div className="page-body justo-appointment-page">
      <Breadcrumb
        title={strings.appointmentManagement}
        parentListing={strings.appointmentList}
        url="/appointment"
        titleactive={strings.todayAppointments}
        isBackBtn={true}
        backurl="/appointment"
      />
      <div className="container-fluid justo-appointment">
        <div className="justo-appointment__card">
          <div className="justo-appointment__top">
            <div className="justo-appointment__title-block">
              <h1 className="justo-appointment__title">
                {strings.todayAppointments}
              </h1>
              <p className="justo-appointment__subtitle">
                {strings.todayAppointmentsSubtitle}
              </p>
            </div>
            <div className="justo-appointment__top-actions">
              {listReady && appointmentData.length > 0 ? (
                <span className="justo-appointment__count-chip">
                  <strong>{appointmentData.length}</strong> appointments
                </span>
              ) : null}
              {create ? (
                <>
                  <button
                    type="button"
                    className="justo-appointment__btn justo-appointment__btn--solid"
                    onClick={() => navigate("/add_appointment")}
                  >
                    {strings.addAppointment}
                  </button>
                  <button
                    type="button"
                    className="justo-appointment__btn justo-appointment__btn--outline"
                    onClick={() => navigate("/appointment")}
                  >
                    {strings.allAppointment}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <div className="justo-appointment__table-wrap">
            <div className="table-responsive appointment-listing--hybrid__scroll">
              {showSkeleton ? (
                <TableListSkeleton rows={10} columns={9} />
              ) : listReady && appointmentData.length === 0 ? (
                <div className="justo-appointment__empty-state">
                  No data available
                </div>
              ) : (
                <table className="table appointment-listing--hybrid__table">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="appointment-listing__text-center appointment-listing__date-col"
                      >
                        Created
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-center appointment-listing__date-col"
                      >
                        Site visit
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-left appointment-listing__property-col"
                      >
                        {strings.property_name}
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-left appointment-listing__visitor-col"
                      >
                        Visitor
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-left appointment-listing__source-col"
                      >
                        {strings.source}
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-center appointment-listing__status-col"
                      >
                        Closing
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-center appointment-listing__status-col"
                      >
                        Check-in
                      </th>
                      <th
                        scope="col"
                        className="appointment-listing__text-center appointment-listing__score-col"
                      >
                        Score
                      </th>
                      <th
                        scope="col"
                        className={`appointment-listing__text-center appointment-listing__actions-col${
                          edit && view
                            ? " appointment-listing__actions-col--wide"
                            : ""
                        }`}
                      >
                        {strings.action}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointmentData.map((item) => {
                      const checkInDisplay = resolveCheckInDisplay(item);
                      const showAsVisited = checkInDisplay.showAsVisited;
                      const visitorName =
                        `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim();
                      const closingLabel = resolveClosingStatusLabel(item);
                      const showEditAction = item.status === 1 && edit;
                      const actionsColClassName =
                        showEditAction && view
                          ? "appointment-listing__text-center appointment-listing__actions-col appointment-listing__actions-col--wide"
                          : "appointment-listing__text-center appointment-listing__actions-col";

                      return (
                        <tr key={item._id}>
                          <td className="appointment-listing__text-center appointment-listing__date-col">
                            {renderDateTimeCell(
                              item.createdDate
                                ? moment(item.createdDate).format("DD-MM-YYYY")
                                : "",
                              item.createdDate
                                ? moment(item.createdDate).format("hh:mm A")
                                : "",
                            )}
                          </td>
                          <td className="appointment-listing__text-center appointment-listing__date-col">
                            {renderDateTimeCell(
                              item.appointment_date
                                ? moment(item.appointment_date)
                                    .utc()
                                    .format("DD-MM-YYYY")
                                : "",
                              item.appointment_time || "",
                            )}
                          </td>
                          <td className="appointment-listing__text-left appointment-listing__property-col">
                            {renderAppointmentCell(
                              item?.property_title,
                              "appointment-listing__primary",
                            )}
                          </td>
                          <td className="appointment-listing__text-left appointment-listing__visitor-col">
                            {renderAppointmentCell(
                              visitorName,
                              "appointment-listing__primary",
                            )}
                          </td>
                          <td className="appointment-listing__text-left appointment-listing__source-col">
                            {renderAppointmentCell(
                              item?.create_by,
                              "appointment-listing__muted",
                            )}
                          </td>
                          <td className="appointment-listing__text-center appointment-listing__status-col">
                            <StatusBadge
                              label={closingLabel}
                              badgeClass={getClosingStatusBadgeClass(
                                closingLabel,
                              )}
                              size="sm"
                            />
                          </td>
                          <td className="appointment-listing__text-center appointment-listing__status-col">
                            <CheckInStatusBadge
                              showAsVisited={showAsVisited}
                              label={checkInDisplay.label}
                            />
                          </td>
                          <td className="appointment-listing__text-center">
                            {renderAppointmentCell(
                              item?.lead_score,
                              "appointment-listing__metric",
                            )}
                          </td>
                          <td className={actionsColClassName}>
                            <div className="appointment-listing__icon-col-inner">
                              <div className="appointment-listing__action-group">
                                {view ? (
                                  <button
                                    type="button"
                                    className="appointment-listing__action-btn"
                                    title="View appointment"
                                    onClick={() =>
                                      navigate(
                                        `/viewappointment/${item._id}`,
                                      )
                                    }
                                  >
                                    <i
                                      className="fa fa-eye"
                                      aria-hidden="true"
                                    />
                                  </button>
                                ) : null}
                                {showEditAction ? (
                                  <button
                                    type="button"
                                    className="appointment-listing__action-btn"
                                    title="Edit appointment"
                                    onClick={() =>
                                      navigate(
                                        `/editappointment/${item._id}`,
                                      )
                                    }
                                  >
                                    <i
                                      className="fa fa-edit"
                                      aria-hidden="true"
                                    />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayAppointment;
